"use client";

import { useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Currency, type AppContext } from "@/types";
import { CashbackOnBanner } from "@/shared/ui/icons/cashback-on-banner";
import { RewardsIcon } from "@/shared/ui/icons/rewards-icon";
import { SmartMembershipIcon } from "@/shared/ui/icons/smart-membership-icon";
import { ChevronRightIcon } from "@/shared/ui/icons/chevron-right-icon";
import { ChevronLeftIcon } from "@/shared/ui/icons/chevron-left-icon";
import MoneyAmount from "@/shared/ui/components/money-amount";
import { SmartBenefitsModal } from "@/modules/plan-selection/components/smart-benefits-modal";

interface AppliedRewards {
  rewardAmount?: {
    amount: number;
    currency?: Currency | string;
  };
  percentageDiscount?: number;
  rewardType?: string;
}

interface PerksBannerProps {
  ctx: AppContext;
  cashbackAmount?: number;
  cashbackEligible?: boolean;
  appliedRewards?: AppliedRewards | null;
  waivedOffReason?: string | null;
  waivedOffFeeAmount?: number;
  processingFeeAmount?: number;
  isFeeWaivedOff?: boolean;
  disabled?: boolean;
  /**
   * `footer` — plan-selection sticky footer (bleed + pull-up).
   * `above-pay` — single-page sticky footer above Pay (horizontal bleed only).
   * `inline` — in-flow content (rounded, padded).
   */
  layout?: "footer" | "above-pay" | "inline";
}

const getPerksBannerScenario = (
  hasWaivedOffFee: boolean,
  hasCashback: boolean,
  hasDiscount: boolean,
  waivedOffFeeAmount: number,
  cashbackAmount: number,
  discountAmount: number,
  currency: string,
  language: string,
  isPartiallyWaivedFee: boolean = false,
): {
  scenario: number;
  components: Record<string, React.ReactNode>;
} => {
  const cpfComponent = (
    <MoneyAmount
      amount={waivedOffFeeAmount}
      currency={currency}
      language={language}
      className="inline-flex mx-0.5 rtl:mx-0.5"
    />
  );
  const cashbackComponent = (
    <MoneyAmount
      amount={cashbackAmount}
      currency={currency}
      language={language}
      className="inline-flex mx-0.5 rtl:mx-0.5"
    />
  );
  const farahComponent = (
    <MoneyAmount
      amount={discountAmount}
      currency={currency}
      language={language}
      className="inline-flex mx-0.5 rtl:mx-0.5"
    />
  );
  const xTotalComponent = (
    <MoneyAmount
      amount={waivedOffFeeAmount + discountAmount}
      currency={currency}
      language={language}
      className="inline-flex mx-0.5 rtl:mx-0.5"
    />
  );

  const flags: string[] = [];
  if (hasWaivedOffFee) flags.push("fee");
  if (hasCashback) flags.push("cashback");
  if (hasDiscount) flags.push("discount");
  const key = flags.join("-") || "none";

  const scenarioMap: Record<
    string,
    { scenario: number; components: Record<string, React.ReactNode> }
  > = {
    "fee-cashback-discount": {
      scenario: isPartiallyWaivedFee ? 11 : 7,
      components: { x_total: xTotalComponent, cashback: cashbackComponent },
    },
    "fee-cashback": {
      scenario: isPartiallyWaivedFee ? 10 : 4,
      components: isPartiallyWaivedFee
        ? { cpf_waived_amount: cpfComponent, cashback: cashbackComponent }
        : { cpf_amount: cpfComponent, cashback: cashbackComponent },
    },
    "fee-discount": {
      scenario: isPartiallyWaivedFee ? 8 : 5,
      components: { x_total: xTotalComponent },
    },
    "cashback-discount": {
      scenario: 6,
      components: { farah: farahComponent, cashback: cashbackComponent },
    },
    fee: {
      scenario: isPartiallyWaivedFee ? 9 : 1,
      components: isPartiallyWaivedFee
        ? { cpf_waived_amount: cpfComponent }
        : { cpf_amount: cpfComponent },
    },
    cashback: {
      scenario: 2,
      components: { cashback: cashbackComponent },
    },
    discount: {
      scenario: 3,
      components: { farah: farahComponent },
    },
  };

  return (
    scenarioMap[key] ?? { scenario: 3, components: { farah: farahComponent } }
  );
};

export function PerksBanner({
  ctx,
  cashbackAmount = 0,
  cashbackEligible = false,
  appliedRewards,
  waivedOffReason,
  waivedOffFeeAmount = 0,
  processingFeeAmount = 0,
  isFeeWaivedOff = false,
  disabled = false,
  layout = "footer",
}: PerksBannerProps) {
  const { i18n } = useTranslation();
  const isInline = layout === "inline";
  const outerClassName =
    layout === "inline"
      ? "mb-4 rounded-xl overflow-hidden"
      : layout === "above-pay"
        ? "-mx-6 mb-4"
        : "-mx-6 -mt-6 mb-4";
  const rowClassName = isInline
    ? "flex items-center min-h-[40px] gap-2 py-2 px-3"
    : "flex justify-center items-center min-h-[40px] gap-1 py-2 px-3";
  const [isSmartBenefitsModalOpen, setIsSmartBenefitsModalOpen] =
    useState(false);

  const hasDiscount = useMemo<boolean>(() => {
    return (
      appliedRewards?.rewardType === "DISCOUNT" &&
      Boolean(appliedRewards?.rewardAmount?.amount) &&
      (appliedRewards?.rewardAmount?.amount ?? 0) > 0
    );
  }, [appliedRewards]);

  const hasCashback = cashbackEligible && cashbackAmount > 0;
  const isSmartWaiver = waivedOffReason === "SMART";
  const isPartiallyWaivedFee =
    isSmartWaiver && isFeeWaivedOff && processingFeeAmount !== 0;
  const hasWaivedOffFee = waivedOffFeeAmount > 0 || isPartiallyWaivedFee;
  const shouldShowSmartBanner =
    waivedOffReason === "SMART" && (hasWaivedOffFee || hasCashback);

  const currency = String(
    ctx.session?.orderTotalAmount?.currency ??
      ctx.session?.order?.currency ??
      Currency.SAR,
  );
  const discountAmount =
    hasDiscount && appliedRewards?.rewardType === "DISCOUNT"
      ? (appliedRewards?.rewardAmount?.amount ?? 0)
      : 0;

  const { scenario, components } = useMemo(
    () =>
      getPerksBannerScenario(
        hasWaivedOffFee,
        hasCashback,
        hasDiscount,
        waivedOffFeeAmount,
        cashbackAmount,
        discountAmount,
        currency,
        i18n.language,
        isPartiallyWaivedFee,
      ),
    [
      hasWaivedOffFee,
      hasCashback,
      hasDiscount,
      waivedOffFeeAmount,
      cashbackAmount,
      discountAmount,
      currency,
      i18n.language,
      isPartiallyWaivedFee,
    ],
  );

  // Don't render if neither discount nor cashback nor waivedOffFee
  if (!hasDiscount && !hasCashback && !hasWaivedOffFee) {
    return null;
  }

  if (shouldShowSmartBanner) {
    return (
      <>
        <div
          className={`${outerClassName} transition-opacity ${disabled ? "opacity-50 pointer-events-none" : ""}`}
          data-testid={
            layout === "inline"
              ? "perks-banner-inline"
              : layout === "above-pay"
                ? "perks-banner-above-pay"
                : undefined
          }
        >
          <button
            type="button"
            onClick={() => setIsSmartBenefitsModalOpen(true)}
            className="block w-full bg-[#FBF3FF] cursor-pointer hover:bg-[#F5E6FF] transition-colors text-start"
            disabled={disabled}
          >
            <div className={rowClassName}>
              <div className="flex items-center">
                <SmartMembershipIcon className="w-[47px] h-4 mr-3 rtl:mr-0 rtl:ml-3 flex-shrink-0" />
                <span className="text-xs font-medium leading-4 tracking-[-0.18px] text-[#9600F1] [word-spacing:0.1em] text-start">
                  <Trans
                    i18nKey={`perks.banner.s${scenario}`}
                    // @ts-expect-error - Trans components type expects a complex union type
                    components={components}
                  />
                </span>
                <div className="ml-1 rtl:ml-0 rtl:mr-1 inline-flex self-center flex-shrink-0 mt-[1.5px]">
                  {i18n.language.startsWith("ar") ? (
                    <ChevronLeftIcon
                      width="11"
                      height="11"
                      fill="#9600F1"
                      stroke="#9600F1"
                    />
                  ) : (
                    <ChevronRightIcon
                      width="11"
                      height="11"
                      fill="#9600F1"
                      stroke="#9600F1"
                    />
                  )}
                </div>
              </div>
            </div>
          </button>
        </div>
        <SmartBenefitsModal
          isOpen={isSmartBenefitsModalOpen}
          onClose={() => setIsSmartBenefitsModalOpen(false)}
          waivedOffFeeAmount={waivedOffFeeAmount}
          cashbackAmount={cashbackAmount}
          rewardAppliedAmount={discountAmount}
          processingFeeAmount={processingFeeAmount}
          currency={currency}
          language={i18n.language}
        />
      </>
    );
  }

  const showCashbackIcon = hasCashback;
  return (
    <div
      className={`${outerClassName} bg-[#DBF9DE] transition-opacity ${disabled ? "opacity-50 pointer-events-none" : ""}`}
      data-testid={
        layout === "inline"
          ? "perks-banner-inline"
          : layout === "above-pay"
            ? "perks-banner-above-pay"
            : undefined
      }
    >
      <div className={rowClassName}>
        {showCashbackIcon ? (
          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
            <CashbackOnBanner className="w-4 h-4" />
          </div>
        ) : (
          <RewardsIcon className="w-4 h-4 [&_path]:fill-[#128351]" />
        )}
        <span className="text-[#128351] text-xs font-medium leading-4 tracking-[-0.18px] [word-spacing:0.1em] text-start">
          <Trans
            i18nKey={`perks.banner.s${scenario}`}
            // @ts-expect-error - Trans components type expects a complex union type
            components={components}
          />
        </span>
      </div>
    </div>
  );
}
